import React, { FormEvent, useState } from 'react'
import { observer } from 'mobx-react-lite'
import clsx from 'clsx'

import { Box, FormHelperText, FormLabel, Grid, Typography } from '@material-ui/core'

import { IOSSwitch } from '../../IOSSwitch'
import { OrderPriceTypePicker } from '../OrderPriceTypePicker'
import { AccountUseMarkingsSwitch } from '../AccountUseMarkingsSwitch'
import { RealizationReportsSwitch } from '../RealizationReportsSwitch'
import { CancelUnreserveSwitch } from '../CancelUnreserveSwitch'
import { AutoShipmentStatusesPicker } from '../AutoShipmentStatusesPicker'

import { useMessages } from '../../../hooks/snackbarHooks'
import { useApi, useLogic } from '../../../hooks/storeHook'
import { useBoolState, useToggleState } from '../../../hooks/commonHooks'
import { useSettingsPageStyles } from '../../../hooks/settingsPageStylesHook'

import { WbAccountProps } from '../../../types/accountProps'
import { IntegrationType } from '../../../server/mpsklad_core/Entity/IntegrationType'
import { OrderPriceType } from '../../../server/mpsklad_core/Entity/Enums/OrderPriceType'

export const WildberriesOrdersSettings =
  observer(({account}: WbAccountProps) => {
    const logic = useLogic()

    const {
      userSync: {
        setWildberriesOrdersSettings
      }
    } = useApi()

    const {showSuccess, showError} = useMessages()
    const [isLoading, setIsLoading, setNotLoading] = useBoolState()

    const [autoConfirmationEnabled, toggleAutoConfirmationEnabled] =
      useToggleState(account.autoConfirmationEnabled)

    const [autoShipmentEnabled, toggleAutoShipmentEnabled] =
      useToggleState(account.autoShipmentEnabled)

    const [autoShipmentStatuses, setAutoShipmentStatuses] =
      useState(account.autoShipmentStatuses)

    const [reserveMsOrdersEnabled, toggleReserveMsOrdersEnabled] =
      useToggleState(account.reserveMsOrdersEnabled)

    const [unreserveOnCancelEnabled, toggleUnreserveOnCancelEnabled] =
      useToggleState(account.unreserveOnCancelEnabled)

    const [isSyncingRealizationReports, toggleSyncingRealizationReports] =
      useToggleState(account.isSyncingRealizationReports)

    const onToggleSyncingRealizationReports = () => {
      if (!isSyncingRealizationReports && !account.msContractId) {
        showError('Для формирования отчётов комиссионера необходимо заполнить поле "Договор" в настройках компании')
        return
      }

      toggleSyncingRealizationReports()
    }

    const [isVatEnabled, toggleIsVatEnabled] =
      useToggleState(account.isVatEnabled)

    const [isGeneratingPayments, toggleIsGeneratingPayments] =
      useToggleState(account.isGeneratingPayments)

    const [convertNonRubleOrders, toggleConvertNonRubleOrders] =
      useToggleState(account.convertNonRubleOrders)

    const [useSerialNumbers, toggleUseSerialNumbers] =
      useToggleState(account.useSerialNumbers)

    const [useMarkingCodes, toggleUseMarkingCodes] =
      useToggleState(account.useMarkingCodes)

    const [orderPriceType, setOrderPriceType] = useState<OrderPriceType>(
      account.orderPriceType ?? OrderPriceType.PriceWithDisc
    )

    const classes = useSettingsPageStyles()

    const onSubmit =
      async (e: FormEvent) => {
        e.preventDefault()

        setIsLoading()

        try {
          await setWildberriesOrdersSettings({
            accountId: account.id,
            isAutoConfirmationEnabled: autoConfirmationEnabled,
            isAutoShipmentEnabled: autoShipmentEnabled,
            autoShipmentStatuses,
            isReserveMsOrdersEnabled: reserveMsOrdersEnabled,
            unreserveOnCancelEnabled,
            isGeneratingPayments,
            isSyncingRealizationReports,
            isConvertNonRubleOrders: convertNonRubleOrders,
            isVatEnabled,
            useSerialNumbers,
            useMarkingCodes,
            orderPriceType
          })

          await logic.loadUserAccounts()

          showSuccess('Сохранено!')
        } catch (ex) {
          console.error('Failed to save Wildberries account', ex)
        } finally {
          setNotLoading()
        }
      }

    return (
      <Box className={classes.pageContainer}>
        <Typography className={classes.header}>Настройки заказов</Typography>

        <Grid container spacing={3}>
          <Grid item xs={9}>
            <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
              <Grid item className={classes.root} xs={12}>
                <Box width={'100%'} display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <FormLabel className={classes.unifiedPricesLabel}>
                      Автоматическая сборка заказов
                    </FormLabel>

                    <FormHelperText className={classes.helperText}>
                      Перевод заказов из статуса "Новый" в статус "Подтверждён".
                    </FormHelperText>
                  </Box>

                  <IOSSwitch checked={autoConfirmationEnabled} onChange={toggleAutoConfirmationEnabled}/>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={9}>
            <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
              <Grid item className={classes.root} xs={12}>
                <Box width={'100%'} display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <FormLabel className={classes.unifiedPricesLabel}>
                      Резерв товаров в заказах МоегоСклада
                    </FormLabel>

                    <FormHelperText className={classes.helperText}>
                      При создании заказа в МоёмСкладе, его позиции будут зарезервированы.
                    </FormHelperText>
                  </Box>

                  <IOSSwitch checked={reserveMsOrdersEnabled} onChange={toggleReserveMsOrdersEnabled} />
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <CancelUnreserveSwitch value={unreserveOnCancelEnabled} onToggle={toggleUnreserveOnCancelEnabled}/>

          <Grid item xs={9}>
            <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
              <Grid item className={classes.root} xs={12}>
                <Box width={'100%'} display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <FormLabel className={classes.unifiedPricesLabel}>
                      Автоматическая отгрузка заказов
                    </FormLabel>

                    <FormHelperText className={classes.helperText}>
                      Создание документов отгрузки при изменении статуса заказа.
                    </FormHelperText>
                  </Box>

                  <IOSSwitch checked={autoShipmentEnabled} onChange={toggleAutoShipmentEnabled}/>
                </Box>
              </Grid>

              <AutoShipmentStatusesPicker
                account={account}
                autoShipmentEnabled={autoShipmentEnabled}
                autoShipmentStatuses={autoShipmentStatuses}
                setAutoShipmentStatuses={setAutoShipmentStatuses}
              />
            </Grid>
          </Grid>

          <Grid item xs={9}>
            <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
              <Grid item className={classes.root} xs={12}>
                <Box width={'100%'} display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <FormLabel className={classes.unifiedPricesLabel}>
                      Конвертировать нерублёвые заказы в рубли
                    </FormLabel>

                  </Box>

                  <IOSSwitch checked={convertNonRubleOrders} onChange={toggleConvertNonRubleOrders}/>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <RealizationReportsSwitch value={isSyncingRealizationReports} onToggle={onToggleSyncingRealizationReports}/>

          <Grid item xs={9}>
            <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
              <Grid item className={classes.root} xs={12}>
                <Box width={'100%'} display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <FormLabel className={classes.unifiedPricesLabel}>
                      Включить учёт НДС
                    </FormLabel>

                  </Box>

                  <IOSSwitch checked={isVatEnabled} onChange={toggleIsVatEnabled}/>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={9}>
            <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
              <Grid item className={classes.root} xs={12}>
                <Box width={'100%'} display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <FormLabel className={classes.unifiedPricesLabel}>
                      Автоматически создавать входящий платёж
                    </FormLabel>

                  </Box>

                  <IOSSwitch checked={isGeneratingPayments} onChange={toggleIsGeneratingPayments}/>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={9}>
            <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
              <Grid item className={classes.root} xs={12}>
                <Box width={'100%'} display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <FormLabel className={classes.unifiedPricesLabel}>
                      Поддержка серийных номеров
                    </FormLabel>

                    <FormHelperText className={classes.helperText}>
                      Необходимо наличие доп. полей "Серийный номер" у заказов, отгрузок и входящих платежей.
                    </FormHelperText>
                  </Box>

                  <IOSSwitch checked={useSerialNumbers} onChange={toggleUseSerialNumbers}/>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <AccountUseMarkingsSwitch account={account} checked={useMarkingCodes} onChange={toggleUseMarkingCodes}/>

          <OrderPriceTypePicker
            storeType={IntegrationType.Wildberries}
            orderPriceType={orderPriceType}
            onOrderPriceTypeChange={setOrderPriceType}/>

        </Grid>

        <Box display="flex">
          <button
            className={clsx(classes.saveButton, 'default-button')}
            disabled={isLoading}
            onClick={onSubmit}>
            <p>{isLoading ? 'Сохраняем...' : 'Сохранить'}</p>
          </button>
        </Box>
      </Box>
    )
  })